import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Chip,
    LinearProgress,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Divider,
    Avatar,
    Tooltip,
    IconButton,
    Collapse,
    Tabs,
    Tab,
    Slider,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Timeline as TimelineIcon,
    BarChart as BarChartIcon,
    PieChart as PieChartIcon,
    Assessment as AssessmentIcon,
    Deck as DeckIcon,
    SportsEsports as SportsEsportsIcon,
    Palette as PaletteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    PlayArrow as PlayArrowIcon,
    Shuffle as ShuffleIcon,
    Person as PersonIcon,
    EmojiEvents as EmojiEventsIcon,
    Psychology as PsychologyIcon,
    School as SchoolIcon,
    ShowChart as ShowChartIcon,
    Analytics as AnalyticsIcon,
    FilterList as FilterListIcon,
    Speed as SpeedIcon
} from '@mui/icons-material';
import { InkColorIcons } from './InkColorIcon';
import { getUserMatchesWithDetails } from '../../services/tracker-limited-data';
import { useAuth } from '../../contexts/AuthContext';
import TrendAnalysisPanel from './TrendAnalysisPanel';
import PerformanceAnalysisPanel from './PerformanceAnalysisPanel';

// Funkcje pomocnicze do analizy danych
const calculateRollingWinrate = (matches, windowSize = 20) => {
    if (matches.length === 0) return [];

    const sortedMatches = [...matches].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const rollingStats = [];

    for (let i = windowSize - 1; i < sortedMatches.length; i++) {
        const windowMatches = sortedMatches.slice(i - windowSize + 1, i + 1);
        const wins = windowMatches.filter(m => m.result === 'win').length;
        const losses = windowMatches.filter(m => m.result === 'lose').length;
        const total = wins + losses;
        const winrate = total > 0 ? (wins / total) * 100 : 0;

        rollingStats.push({
            gameNumber: i + 1,
            winrate: winrate,
            wins: wins,
            losses: losses,
            total: total,
            timestamp: sortedMatches[i].timestamp
        });
    }

    return rollingStats;
};

const calculateArchetypeMatchups = (matches) => {
    const matchups = {};

    matches.forEach(match => {
        if (!match.myDeckInfo?.archetype || !match.opponentArchetypeInfo) return;

        const myArchetype = match.myDeckInfo.archetype;
        const opponentArchetype = match.opponentArchetypeInfo;

        const key = `${myArchetype.id}-vs-${opponentArchetype.id}`;

        if (!matchups[key]) {
            matchups[key] = {
                myArchetype: myArchetype,
                opponentArchetype: opponentArchetype,
                games: 0,
                wins: 0,
                losses: 0,
                draws: 0,
                playGames: 0,
                playWins: 0,
                playLosses: 0,
                drawGames: 0,
                drawWins: 0,
                drawLosses: 0
            };
        }

        const matchup = matchups[key];
        matchup.games++;

        if (match.result === 'win') matchup.wins++;
        else if (match.result === 'lose') matchup.losses++;
        else if (match.result === 'draw') matchup.draws++;

        if (match.playDraw === 'play') {
            matchup.playGames++;
            if (match.result === 'win') matchup.playWins++;
            else if (match.result === 'lose') matchup.playLosses++;
        } else if (match.playDraw === 'draw') {
            matchup.drawGames++;
            if (match.result === 'win') matchup.drawWins++;
            else if (match.result === 'lose') matchup.drawLosses++;
        }
    });

    // Oblicz winrate dla każdego matchup
    Object.values(matchups).forEach(matchup => {
        const totalGames = matchup.wins + matchup.losses;
        matchup.winrate = totalGames > 0 ? (matchup.wins / totalGames) * 100 : 0;
        matchup.playWinrate = matchup.playGames > 0 ? (matchup.playWins / matchup.playGames) * 100 : 0;
        matchup.drawWinrate = matchup.drawGames > 0 ? (matchup.drawWins / matchup.drawGames) * 100 : 0;
    });

    return matchups;
};

const calculateArchetypePerformance = (matches) => {
    const performance = {};

    matches.forEach(match => {
        if (!match.myDeckInfo?.archetype) return;

        const archetype = match.myDeckInfo.archetype;
        const archetypeId = archetype.id;

        if (!performance[archetypeId]) {
            performance[archetypeId] = {
                archetype: archetype,
                games: 0,
                wins: 0,
                losses: 0,
                draws: 0,
                playGames: 0,
                playWins: 0,
                playLosses: 0,
                drawGames: 0,
                drawWins: 0,
                drawLosses: 0,
                recentGames: [],
                monthlyStats: {}
            };
        }

        const stats = performance[archetypeId];
        stats.games++;

        if (match.result === 'win') stats.wins++;
        else if (match.result === 'lose') stats.losses++;
        else if (match.result === 'draw') stats.draws++;

        if (match.playDraw === 'play') {
            stats.playGames++;
            if (match.result === 'win') stats.playWins++;
            else if (match.result === 'lose') stats.playLosses++;
        } else if (match.playDraw === 'draw') {
            stats.drawGames++;
            if (match.result === 'win') stats.drawWins++;
            else if (match.result === 'lose') stats.drawLosses++;
        }

        // Dodaj do ostatnich gier
        stats.recentGames.push({
            result: match.result,
            opponent: match.opponentArchetypeInfo?.name || 'Nieznany',
            timestamp: match.timestamp,
            playDraw: match.playDraw
        });

        // Statystyki miesięczne
        const date = new Date(match.timestamp);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (!stats.monthlyStats[monthKey]) {
            stats.monthlyStats[monthKey] = { games: 0, wins: 0, losses: 0, draws: 0 };
        }

        const monthStats = stats.monthlyStats[monthKey];
        monthStats.games++;
        if (match.result === 'win') monthStats.wins++;
        else if (match.result === 'lose') monthStats.losses++;
        else if (match.result === 'draw') monthStats.draws++;
    });

    // Oblicz winrate i posortuj ostatnie gry
    Object.values(performance).forEach(stats => {
        const totalGames = stats.wins + stats.losses;
        stats.winrate = totalGames > 0 ? (stats.wins / totalGames) * 100 : 0;
        stats.playWinrate = stats.playGames > 0 ? (stats.playWins / stats.playGames) * 100 : 0;
        stats.drawWinrate = stats.drawGames > 0 ? (stats.drawWins / stats.drawGames) * 100 : 0;

        // Posortuj ostatnie gry (najnowsze pierwsze)
        stats.recentGames.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        // Oblicz winrate dla ostatnich 10 gier
        const last10Games = stats.recentGames.slice(0, 10);
        const last10Wins = last10Games.filter(g => g.result === 'win').length;
        const last10Losses = last10Games.filter(g => g.result === 'lose').length;
        const last10Total = last10Wins + last10Losses;
        stats.last10Winrate = last10Total > 0 ? (last10Wins / last10Total) * 100 : 0;
    });

    return performance;
};

// Komponent wykresu winrate w czasie
function WinrateTimelineChart({ matches, windowSize = 20 }) {
    const theme = useTheme();
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const rollingStats = calculateRollingWinrate(matches, windowSize);

    if (rollingStats.length === 0) {
        return (
            <Card sx={{
                background: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '16px'
            }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <ShowChartIcon sx={{ color: 'rgba(196, 181, 253, 0.5)', fontSize: 48, mb: 1.5 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                        Brak danych do wykresu
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                        Potrzebujesz co najmniej {windowSize} meczów aby zobaczyć trend winrate
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const maxWinrate = Math.max(...rollingStats.map(s => s.winrate));
    const minWinrate = Math.min(...rollingStats.map(s => s.winrate));
    const avgWinrate = rollingStats.reduce((sum, s) => sum + s.winrate, 0) / rollingStats.length;

    return (
        <Card sx={{
            background: 'rgba(17, 24, 39, 0.8)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(76, 29, 149, 0.3)',
            borderRadius: '16px'
        }}>
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <ShowChartIcon sx={{ color: '#7c3aed', mr: 1, fontSize: 24 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Winrate w czasie (ostatnie {windowSize} gier)
                    </Typography>
                </Box>

                {/* Statystyki podsumowujące */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 700 }}>
                                {maxWinrate.toFixed(1)}%
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                Maksymalny
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                                {avgWinrate.toFixed(1)}%
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                Średni
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ color: '#ef4444', fontWeight: 700 }}>
                                {minWinrate.toFixed(1)}%
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                Minimalny
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                {/* Wykres słupkowy */}
                <Box sx={{ height: 200, display: 'flex', alignItems: 'end', gap: 1, overflowX: 'auto' }}>
                    {rollingStats.map((stat, index) => {
                        const height = (stat.winrate / 100) * 180;
                        const color = stat.winrate >= 60 ? '#10b981' : stat.winrate >= 50 ? '#f59e0b' : '#ef4444';

                        return (
                            <Tooltip
                                key={index}
                                title={
                                    <Box>
                                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                            Mecz #{stat.gameNumber}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'white' }}>
                                            Winrate: {stat.winrate.toFixed(1)}%
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'white' }}>
                                            {stat.wins}W / {stat.losses}L
                                        </Typography>
                                    </Box>
                                }
                            >
                                <Box
                                    sx={{
                                        width: isSmallMobile ? 8 : 12,
                                        height: height,
                                        backgroundColor: color,
                                        borderRadius: '2px 2px 0 0',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            opacity: 0.8,
                                            transform: 'scaleY(1.1)'
                                        }
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Box>

                {/* Ostatni winrate */}
                {rollingStats.length > 0 && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                            Aktualny winrate: {rollingStats[rollingStats.length - 1].winrate.toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                            Na podstawie ostatnich {windowSize} meczów
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

// Komponent analizy matchup archetypów
function ArchetypeMatchupAnalysis({ matches }) {
    const theme = useTheme();
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const matchups = calculateArchetypeMatchups(matches);
    const matchupArray = Object.values(matchups)
        .sort((a, b) => b.games - a.games)
        .slice(0, 10); // Top 10 matchup

    if (matchupArray.length === 0) {
        return (
            <Card sx={{
                background: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '16px'
            }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <PsychologyIcon sx={{ color: 'rgba(196, 181, 253, 0.5)', fontSize: 48, mb: 1.5 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                        Brak danych o matchup
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                        Rozpocznij grę różnymi archetypami przeciwko różnym przeciwnikom
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{
            background: 'rgba(17, 24, 39, 0.8)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(76, 29, 149, 0.3)',
            borderRadius: '16px'
        }}>
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <PsychologyIcon sx={{ color: '#3b82f6', mr: 1, fontSize: 24 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Analiza Matchup Archetypów
                    </Typography>
                </Box>

                {isSmallMobile ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {matchupArray.map((matchup, index) => (
                            <Card key={index} sx={{
                                background: 'rgba(17, 24, 39, 0.5)',
                                border: '1px solid rgba(76, 29, 149, 0.2)',
                                borderRadius: '12px'
                            }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                                {matchup.myArchetype.name} vs {matchup.opponentArchetype.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <InkColorIcons
                                                    colors={matchup.myArchetype.ink_colors || []}
                                                    size={16}
                                                    gap={0.5}
                                                />
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                                    vs
                                                </Typography>
                                                <InkColorIcons
                                                    colors={matchup.opponentArchetype.ink_colors || []}
                                                    size={16}
                                                    gap={0.5}
                                                />
                                            </Box>
                                        </Box>
                                        <Chip
                                            label={`${matchup.winrate.toFixed(1)}%`}
                                            size="small"
                                            sx={{
                                                backgroundColor: matchup.winrate >= 60 ? '#10b981' : matchup.winrate >= 50 ? '#f59e0b' : '#ef4444',
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                                        {matchup.games} meczów ({matchup.wins}W / {matchup.losses}L)
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                            Play: {matchup.playGames} ({matchup.playWinrate.toFixed(1)}%)
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                            Draw: {matchup.drawGames} ({matchup.drawWinrate.toFixed(1)}%)
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600 }}>
                                        Matchup
                                    </TableCell>
                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600 }}>
                                        Mecze
                                    </TableCell>
                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600 }}>
                                        Winrate
                                    </TableCell>
                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600 }}>
                                        Play/Draw
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {matchupArray.map((matchup, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                                                    {matchup.myArchetype.name} vs {matchup.opponentArchetype.name}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <InkColorIcons
                                                        colors={matchup.myArchetype.ink_colors || []}
                                                        size={16}
                                                        gap={0.5}
                                                    />
                                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                                        vs
                                                    </Typography>
                                                    <InkColorIcons
                                                        colors={matchup.opponentArchetype.ink_colors || []}
                                                        size={16}
                                                        gap={0.5}
                                                    />
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: 'white' }}>
                                            {matchup.games} ({matchup.wins}W / {matchup.losses}L)
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={`${matchup.winrate.toFixed(1)}%`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: matchup.winrate >= 60 ? '#10b981' : matchup.winrate >= 50 ? '#f59e0b' : '#ef4444',
                                                    color: 'white',
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ color: 'white' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                                    Play: {matchup.playGames} ({matchup.playWinrate.toFixed(1)}%)
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                                    Draw: {matchup.drawGames} ({matchup.drawWinrate.toFixed(1)}%)
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
}

// Komponent szczegółowej analizy archetypów
function DetailedArchetypeAnalysis({ matches }) {
    const theme = useTheme();
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const performance = calculateArchetypePerformance(matches);
    const performanceArray = Object.values(performance)
        .sort((a, b) => b.games - a.games);

    if (performanceArray.length === 0) {
        return (
            <Card sx={{
                background: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '16px'
            }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <PaletteIcon sx={{ color: 'rgba(196, 181, 253, 0.5)', fontSize: 48, mb: 1.5 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                        Brak danych o archetypach
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                        Rozpocznij grę różnymi archetypami aby zobaczyć szczegółową analizę
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{
            background: 'rgba(17, 24, 39, 0.8)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(76, 29, 149, 0.3)',
            borderRadius: '16px'
        }}>
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <PaletteIcon sx={{ color: '#10b981', mr: 1, fontSize: 24 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Szczegółowa Analiza Archetypów
                    </Typography>
                </Box>

                {isSmallMobile ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {performanceArray.map((stats, index) => (
                            <Card key={index} sx={{
                                background: 'rgba(17, 24, 39, 0.5)',
                                border: '1px solid rgba(76, 29, 149, 0.2)',
                                borderRadius: '12px'
                            }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                                {stats.archetype.name}
                                            </Typography>
                                            <InkColorIcons
                                                colors={stats.archetype.ink_colors || []}
                                                size={16}
                                                gap={0.5}
                                            />
                                        </Box>
                                        <Chip
                                            label={`${stats.winrate.toFixed(1)}%`}
                                            size="small"
                                            sx={{
                                                backgroundColor: stats.winrate >= 60 ? '#10b981' : stats.winrate >= 50 ? '#f59e0b' : '#ef4444',
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>

                                    <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                                        {stats.games} meczów ({stats.wins}W / {stats.losses}L)
                                    </Typography>

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                            Ostatnie 10 gier: {stats.last10Winrate.toFixed(1)}%
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                            Play: {stats.playGames} ({stats.playWinrate.toFixed(1)}%)
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                            Draw: {stats.drawGames} ({stats.drawWinrate.toFixed(1)}%)
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600 }}>
                                        Archetyp
                                    </TableCell>
                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600 }}>
                                        Mecze
                                    </TableCell>
                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600 }}>
                                        Winrate
                                    </TableCell>
                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600 }}>
                                        Ostatnie 10
                                    </TableCell>
                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600 }}>
                                        Play/Draw
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {performanceArray.map((stats, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                                                    {stats.archetype.name}
                                                </Typography>
                                                <InkColorIcons
                                                    colors={stats.archetype.ink_colors || []}
                                                    size={16}
                                                    gap={0.5}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: 'white' }}>
                                            {stats.games} ({stats.wins}W / {stats.losses}L)
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={`${stats.winrate.toFixed(1)}%`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: stats.winrate >= 60 ? '#10b981' : stats.winrate >= 50 ? '#f59e0b' : '#ef4444',
                                                    color: 'white',
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={`${stats.last10Winrate.toFixed(1)}%`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: stats.last10Winrate >= 60 ? '#10b981' : stats.last10Winrate >= 50 ? '#f59e0b' : '#ef4444',
                                                    color: 'white',
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ color: 'white' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                                    Play: {stats.playGames} ({stats.playWinrate.toFixed(1)}%)
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                                    Draw: {stats.drawGames} ({stats.drawWinrate.toFixed(1)}%)
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
}

// Główny komponent
function AdvancedStatsPanel() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { currentUser } = useAuth();

    const [matches, setMatches] = useState([]);
    const [filteredMatches, setFilteredMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [windowSize, setWindowSize] = useState(20);
    const [showAllMatchups, setShowAllMatchups] = useState(false);

    // Filtry
    const [filters, setFilters] = useState({
        timeRange: 'all', // all, week, month, 3months, 6months, year
        archetype: 'all', // all, specific archetype
        deck: 'all', // all, specific deck
        result: 'all', // all, win, lose, draw
        playDraw: 'all', // all, play, draw
        minGames: 0,
        maxGames: 1000
    });

    useEffect(() => {
        loadMatches();
    }, [currentUser]);

    useEffect(() => {
        applyFilters();
    }, [matches, filters]);

    const loadMatches = async () => {
        if (!currentUser) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const matchesData = await getUserMatchesWithDetails(currentUser.uid);
            setMatches(matchesData);
        } catch (err) {
            console.error('Błąd podczas ładowania meczy:', err);
            setError('Nie udało się załadować meczy');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...matches];

        // Filtr czasowy
        if (filters.timeRange !== 'all') {
            const now = new Date();
            const cutoffDate = new Date();

            switch (filters.timeRange) {
                case 'week':
                    cutoffDate.setDate(now.getDate() - 7);
                    break;
                case 'month':
                    cutoffDate.setMonth(now.getMonth() - 1);
                    break;
                case '3months':
                    cutoffDate.setMonth(now.getMonth() - 3);
                    break;
                case '6months':
                    cutoffDate.setMonth(now.getMonth() - 6);
                    break;
                case 'year':
                    cutoffDate.setFullYear(now.getFullYear() - 1);
                    break;
            }

            filtered = filtered.filter(match => new Date(match.timestamp) >= cutoffDate);
        }

        // Filtr archetypu
        if (filters.archetype !== 'all') {
            filtered = filtered.filter(match =>
                match.myDeckInfo?.archetype?.id === filters.archetype
            );
        }

        // Filtr talii
        if (filters.deck !== 'all') {
            filtered = filtered.filter(match =>
                match.myDeckId === filters.deck
            );
        }

        // Filtr wyniku
        if (filters.result !== 'all') {
            filtered = filtered.filter(match => match.result === filters.result);
        }

        // Filtr play/draw
        if (filters.playDraw !== 'all') {
            filtered = filtered.filter(match => match.playDraw === filters.playDraw);
        }

        setFilteredMatches(filtered);
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    const resetFilters = () => {
        setFilters({
            timeRange: 'all',
            archetype: 'all',
            deck: 'all',
            result: 'all',
            playDraw: 'all',
            minGames: 0,
            maxGames: 1000
        });
    };

    // Pobierz unikalne archetypy i talie dla filtrów
    const getUniqueArchetypes = () => {
        const archetypes = new Map();
        matches.forEach(match => {
            if (match.myDeckInfo?.archetype) {
                const arch = match.myDeckInfo.archetype;
                archetypes.set(arch.id, arch);
            }
        });
        return Array.from(archetypes.values());
    };

    const getUniqueDecks = () => {
        const decks = new Map();
        matches.forEach(match => {
            if (match.myDeckInfo) {
                decks.set(match.myDeckId, match.myDeckInfo);
            }
        });
        return Array.from(decks.values());
    };

    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                    Ładowanie zaawansowanych statystyk...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" sx={{ color: '#ef4444' }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    if (matches.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                    Brak meczy do analizy
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(156, 163, 175, 0.7)', mt: 1 }}>
                    Zarejestruj mecze aby zobaczyć zaawansowane statystyki
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: isSmallMobile ? 'flex-start' : 'center',
                mb: 3,
                flexDirection: isSmallMobile ? 'column' : 'row',
                gap: isSmallMobile ? 2 : 0
            }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                    Zaawansowane Statystyki
                </Typography>

            </Box>

            {/* Panel filtrów */}
            <Card sx={{
                background: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '16px',
                mb: 3
            }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FilterListIcon sx={{ color: '#7c3aed', mr: 1, fontSize: 24 }} />
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                Filtry Analizy
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={resetFilters}
                            sx={{
                                color: 'rgba(196, 181, 253, 0.7)',
                                borderColor: 'rgba(76, 29, 149, 0.5)',
                                '&:hover': {
                                    borderColor: '#7c3aed',
                                    backgroundColor: 'rgba(76, 29, 149, 0.1)'
                                }
                            }}
                        >
                            Resetuj filtry
                        </Button>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Okres
                                </InputLabel>
                                <Select
                                    value={filters.timeRange}
                                    onChange={(e) => handleFilterChange('timeRange', e.target.value)}
                                    label="Okres"
                                    sx={{
                                        color: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.8)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7c3aed'
                                        }
                                    }}
                                >
                                    <MenuItem value="all">Wszystkie</MenuItem>
                                    <MenuItem value="week">Ostatni tydzień</MenuItem>
                                    <MenuItem value="month">Ostatni miesiąc</MenuItem>
                                    <MenuItem value="3months">Ostatnie 3 miesiące</MenuItem>
                                    <MenuItem value="6months">Ostatnie 6 miesięcy</MenuItem>
                                    <MenuItem value="year">Ostatni rok</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Archetyp
                                </InputLabel>
                                <Select
                                    value={filters.archetype}
                                    onChange={(e) => handleFilterChange('archetype', e.target.value)}
                                    label="Archetyp"
                                    sx={{
                                        color: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.8)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7c3aed'
                                        }
                                    }}
                                >
                                    <MenuItem value="all">Wszystkie</MenuItem>
                                    {getUniqueArchetypes().map(archetype => (
                                        <MenuItem key={archetype.id} value={archetype.id}>
                                            {archetype.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Talia
                                </InputLabel>
                                <Select
                                    value={filters.deck}
                                    onChange={(e) => handleFilterChange('deck', e.target.value)}
                                    label="Talia"
                                    sx={{
                                        color: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.8)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7c3aed'
                                        }
                                    }}
                                >
                                    <MenuItem value="all">Wszystkie</MenuItem>
                                    {getUniqueDecks().map(deck => (
                                        <MenuItem key={deck.id} value={deck.id}>
                                            {deck.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Wynik
                                </InputLabel>
                                <Select
                                    value={filters.result}
                                    onChange={(e) => handleFilterChange('result', e.target.value)}
                                    label="Wynik"
                                    sx={{
                                        color: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.8)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7c3aed'
                                        }
                                    }}
                                >
                                    <MenuItem value="all">Wszystkie</MenuItem>
                                    <MenuItem value="win">Wygrane</MenuItem>
                                    <MenuItem value="lose">Przegrane</MenuItem>
                                    <MenuItem value="draw">Remisy</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Play/Draw
                                </InputLabel>
                                <Select
                                    value={filters.playDraw}
                                    onChange={(e) => handleFilterChange('playDraw', e.target.value)}
                                    label="Play/Draw"
                                    sx={{
                                        color: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.8)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7c3aed'
                                        }
                                    }}
                                >
                                    <MenuItem value="all">Wszystkie</MenuItem>
                                    <MenuItem value="play">Play</MenuItem>
                                    <MenuItem value="draw">Draw</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
                                <Chip
                                    label={`${filteredMatches.length} meczów`}
                                    sx={{
                                        backgroundColor: 'rgba(76, 29, 149, 0.2)',
                                        color: 'white',
                                        fontWeight: 600
                                    }}
                                />
                                {filteredMatches.length !== matches.length && (
                                    <Chip
                                        label={`z ${matches.length}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                            color: '#f59e0b',
                                            fontWeight: 600
                                        }}
                                    />
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Panel opcji analizy */}
            <Card sx={{
                background: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '16px',
                mb: 3
            }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AnalyticsIcon sx={{ color: '#7c3aed', mr: 1, fontSize: 24 }} />
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                            Opcje Analizy
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Okno analizy
                                </InputLabel>
                                <Select
                                    value={windowSize}
                                    onChange={(e) => setWindowSize(e.target.value)}
                                    label="Okno analizy"
                                    sx={{
                                        color: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.8)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7c3aed'
                                        }
                                    }}
                                >
                                    <MenuItem value={5}>Ostatnie 5 gier</MenuItem>
                                    <MenuItem value={10}>Ostatnie 10 gier</MenuItem>
                                    <MenuItem value={20}>Ostatnie 20 gier</MenuItem>
                                    <MenuItem value={50}>Ostatnie 50 gier</MenuItem>
                                    <MenuItem value={100}>Ostatnie 100 gier</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showAllMatchups}
                                        onChange={(e) => setShowAllMatchups(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#7c3aed',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#7c3aed',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        Pokaż wszystkie matchup
                                    </Typography>
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mr: 1 }}>
                                    Zakres dat:
                                </Typography>
                                <Chip
                                    label={filteredMatches.length > 0 ?
                                        `${new Date(Math.min(...filteredMatches.map(m => new Date(m.timestamp)))).toLocaleDateString('pl-PL')} - ${new Date(Math.max(...filteredMatches.map(m => new Date(m.timestamp)))).toLocaleDateString('pl-PL')}` :
                                        'Brak danych'
                                    }
                                    size="small"
                                    sx={{
                                        backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                        color: '#10b981',
                                        fontWeight: 600
                                    }}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mr: 1 }}>
                                    Winrate:
                                </Typography>
                                <Chip
                                    label={filteredMatches.length > 0 ?
                                        `${((filteredMatches.filter(m => m.result === 'win').length / filteredMatches.filter(m => m.result === 'win' || m.result === 'lose').length) * 100).toFixed(1)}%` :
                                        '0%'
                                    }
                                    size="small"
                                    sx={{
                                        backgroundColor: filteredMatches.length > 0 &&
                                            ((filteredMatches.filter(m => m.result === 'win').length / filteredMatches.filter(m => m.result === 'win' || m.result === 'lose').length) * 100) >= 60 ?
                                            'rgba(16, 185, 129, 0.2)' :
                                            ((filteredMatches.filter(m => m.result === 'win').length / filteredMatches.filter(m => m.result === 'win' || m.result === 'lose').length) * 100) >= 50 ?
                                                'rgba(245, 158, 11, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                        color: filteredMatches.length > 0 &&
                                            ((filteredMatches.filter(m => m.result === 'win').length / filteredMatches.filter(m => m.result === 'win' || m.result === 'lose').length) * 100) >= 60 ?
                                            '#10b981' :
                                            ((filteredMatches.filter(m => m.result === 'win').length / filteredMatches.filter(m => m.result === 'win' || m.result === 'lose').length) * 100) >= 50 ?
                                                '#f59e0b' : '#ef4444',
                                        fontWeight: 600
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Zakładki */}
            <Card sx={{
                background: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '16px',
                mb: 3
            }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant={isSmallMobile ? "scrollable" : "standard"}
                    scrollButtons={isSmallMobile ? "auto" : false}
                    sx={{
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#7c3aed'
                        },
                        '& .MuiTab-root': {
                            color: 'rgba(196, 181, 253, 0.7)',
                            fontWeight: 600,
                            textTransform: 'none',
                            minHeight: isSmallMobile ? 48 : 64,
                            fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                            '&.Mui-selected': {
                                color: 'white'
                            }
                        }
                    }}
                >
                    <Tab
                        label={isSmallMobile ? "Trend" : "Trend Winrate"}
                        icon={<ShowChartIcon sx={{ fontSize: isSmallMobile ? 20 : 24 }} />}
                        iconPosition="start"
                    />
                    <Tab
                        label={isSmallMobile ? "Matchup" : "Analiza Matchup"}
                        icon={<PsychologyIcon sx={{ fontSize: isSmallMobile ? 20 : 24 }} />}
                        iconPosition="start"
                    />
                    <Tab
                        label={isSmallMobile ? "Archetypy" : "Szczegółowa analiza archetypów"}
                        icon={<PaletteIcon sx={{ fontSize: isSmallMobile ? 20 : 24 }} />}
                        iconPosition="start"
                    />
                    <Tab
                        label={isSmallMobile ? "Trendy" : "Analiza trendów"}
                        icon={<TimelineIcon sx={{ fontSize: isSmallMobile ? 20 : 24 }} />}
                        iconPosition="start"
                    />
                    <Tab
                        label={isSmallMobile ? "Wydajność" : "Analiza wydajności"}
                        icon={<SpeedIcon sx={{ fontSize: isSmallMobile ? 20 : 24 }} />}
                        iconPosition="start"
                    />
                </Tabs>
            </Card>

            {/* Zawartość zakładek */}
            {activeTab === 0 && (
                <WinrateTimelineChart matches={filteredMatches} windowSize={windowSize} />
            )}

            {activeTab === 1 && (
                <ArchetypeMatchupAnalysis matches={filteredMatches} />
            )}

            {activeTab === 2 && (
                <DetailedArchetypeAnalysis matches={filteredMatches} />
            )}

            {activeTab === 3 && (
                <TrendAnalysisPanel matches={filteredMatches} />
            )}

            {activeTab === 4 && (
                <PerformanceAnalysisPanel matches={filteredMatches} />
            )}
        </Box>
    );
}

export default AdvancedStatsPanel;
